import HashQuery from '../HashQuery/HashQuery';

// Short key to keep the param in line with the other short URL params.
const mapLayersUrlKey = 'l';

// Aliases used only in the URL so shared links stay tidy.
const layerUrlAliases: { [layerId: string]: string } = {
    'show-current-bike-situations': 'aktuella',
    'show-planned-bike-situations': 'planerade',
    'rental-bike': 'lanecykel',
    'pump-station': 'pump',
    'bike-parkings-gbg': 'parkering',
    'bike-roads-gbg': 'cykelvag',
};

const aliasToLayerId: { [alias: string]: string } = Object.fromEntries(Object.entries(layerUrlAliases).map(([layerId, alias]) => [alias, layerId]));

const toUrlAlias = (layerId: string): string => layerUrlAliases[layerId] ?? layerId;
const fromUrlAlias = (alias: string): string => aliasToLayerId[alias] ?? alias;

// Reads the active map layers from the URL hash (used to make filter selections shareable via link).
export const getUrlMapLayers = function (): { [layerId: string]: boolean } | undefined {
    const raw = HashQuery.getQueryByKey(mapLayersUrlKey);
    if (!raw || typeof raw !== 'string') {
        return undefined;
    }
    return raw.split(',').reduce<{ [layerId: string]: boolean }>((layers, token) => {
        if (token) {
            if (token.startsWith('!')) {
                layers[fromUrlAlias(token.slice(1))] = false;
            } else {
                layers[fromUrlAlias(token)] = true;
            }
        }
        return layers;
    }, {});
};

// Persists a single map layer toggle to the URL hash instead of local storage.
// Only layers whose state differs from the default are stored: a default-on layer that is
// turned off is kept as `!layerId`, a non-default layer that is turned on is kept as `layerId`.
// Anything matching its default is dropped from the URL to keep it clean.
export const setUrlMapLayer = function (layerId: string, enabled: boolean, defaultOnLayers: string[] = []): void {
    const layers = getUrlMapLayers() || {};
    const isDefaultOn = defaultOnLayers.includes(layerId);

    if (enabled === isDefaultOn) {
        delete layers[layerId];
    } else {
        layers[layerId] = enabled;
    }

    const encoded = Object.keys(layers)
        .map((id) => (layers[id] ? toUrlAlias(id) : '!' + toUrlAlias(id)))
        .join(',');

    if (encoded) {
        HashQuery.setQueryValue(mapLayersUrlKey, encoded, false);
    } else {
        HashQuery.removeQueryKey(mapLayersUrlKey, false);
    }
};
